import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreditCard, CheckCircle2, AlertCircle, Loader2, Sparkles,
  Building2, ShieldCheck, Zap, ArrowUpRight, RotateCcw, RefreshCw,
  Calendar, Receipt,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtNumber, getLocaleSettings } from '../lib/localeUtils';

const TIER_META = {
  essentials: { color: 'emerald', icon: Zap, accent: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  professional: { color: 'sky', icon: Sparkles, accent: 'bg-sky-500/10 text-sky-600 border-sky-500/20' },
};

function fmtGHS(pesewasOrGhs, isPesewa = false) {
  const n = isPesewa ? pesewasOrGhs / 100 : pesewasOrGhs;
  // Platform subscription billing is always GHS (via Paystack), regardless of
  // the pharmacy's own operational currency — only the number grouping
  // (comma vs period) should follow the tenant's saved locale.
  const numberLocale = getLocaleSettings().number_locale;
  const formatted = Number(n || 0).toLocaleString(numberLocale, { maximumFractionDigits: 0 });
  return `GH₵${formatted}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [confirmTier, setConfirmTier] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [acting, setActing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, i] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/subscription'),
        api.get('/billing/invoices'),
      ]);
      setPlans(p.data?.plans || []);
      setSubscription(s.data?.subscription || null);
      setInvoices(i.data?.invoices || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load subscription state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startCheckout = async (tierKey, cycle) => {
    if (!isAdmin) {
      toast.error('Only super admin / admin can manage the subscription');
      return;
    }
    setRedirecting(true);
    try {
      const callbackUrl = `${window.location.origin}/subscription/callback`;
      const { data } = await api.post('/billing/checkout', {
        tier: tierKey, cycle, callback_url: callbackUrl,
      });
      sessionStorage.setItem('pharmxai_pending_ref', data.reference);
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start payment');
      setRedirecting(false);
    }
  };

  const cancel = async () => {
    setActing(true);
    try {
      const { data } = await api.post('/billing/cancel');
      setSubscription(data.subscription);
      toast.success('Subscription will cancel at the end of the current period');
      setCancelOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally {
      setActing(false);
    }
  };

  const reactivate = async () => {
    setActing(true);
    try {
      const { data } = await api.post('/billing/reactivate');
      setSubscription(data.subscription);
      toast.success('Subscription reactivated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reactivate failed');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading subscription…
      </div>
    );
  }

  const currentTierKey = subscription?.tier;
  const isActive = subscription && subscription.status === 'active';
  const willCancel = subscription?.cancel_at_period_end;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="subscription-page">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-emerald-600" />
            Subscription & Billing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your plan, see invoices and update payment method.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={reload} className="gap-1.5" data-testid="sub-refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </header>

      {!isAdmin && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          You are viewing the subscription in read-only mode. Only super admin / admin can change the plan.
        </div>
      )}

      {/* ── Current plan ── */}
      <CurrentPlanCard
        subscription={subscription}
        onCancel={() => setCancelOpen(true)}
        onReactivate={reactivate}
        acting={acting}
        isAdmin={isAdmin}
      />

      {/* ── Plan selector ── */}
      <section className="space-y-3" data-testid="plan-selector">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold tracking-tight">{isActive ? 'Change plan' : 'Choose a plan'}</h2>
            <p className="text-sm text-muted-foreground">All prices in GHS. Annual billing saves 20%.</p>
          </div>
          <div className="inline-flex items-center rounded-full border bg-muted p-1">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1 rounded-full text-xs font-medium transition-colors ${
                billingCycle === 'monthly' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="cycle-monthly-btn"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                billingCycle === 'annual' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="cycle-annual-btn"
            >
              Annual
              <span className="text-[9px] font-bold bg-orange-400 text-orange-950 rounded-full px-1.5">−20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const meta = TIER_META[p.key] || TIER_META.essentials;
            const Icon = meta.icon;
            const price = billingCycle === 'monthly' ? p.monthly_ghs : p.annual_ghs;
            const isCurrent = currentTierKey === p.key && subscription?.cycle === billingCycle && isActive;
            const ctaLabel = isCurrent
              ? 'Current plan'
              : isActive
                ? (subscription?.tier === 'essentials' && p.key === 'professional' ? 'Upgrade' : 'Switch')
                : 'Subscribe';

            return (
              <div
                key={p.key}
                data-testid={`plan-card-${p.key}`}
                className={`relative rounded-2xl border bg-card p-6 hover:border-emerald-300 transition-colors ${
                  isCurrent ? 'border-emerald-500 ring-1 ring-emerald-500/30' : ''
                }`}
              >
                {isCurrent && (
                  <span className="absolute -top-2 right-4 text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
                <div className={`h-10 w-10 rounded-lg border ${meta.accent} flex items-center justify-center mb-4`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold">{p.name}</h3>
                <p className="text-xs text-muted-foreground mb-4 min-h-[32px]">{p.tagline}</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold tabular-nums">{fmtGHS(price)}</span>
                  <span className="text-xs text-muted-foreground ml-1">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>
                <Button
                  className="w-full"
                  variant={isCurrent ? 'outline' : 'default'}
                  disabled={isCurrent || !isAdmin || redirecting}
                  onClick={() => setConfirmTier({ tier: p.key, cycle: billingCycle, price })}
                  data-testid={`subscribe-${p.key}-btn`}
                >
                  {ctaLabel}
                  {!isCurrent && <ArrowUpRight className="h-3.5 w-3.5 ml-1" />}
                </Button>
                <ul className="mt-5 space-y-1.5 text-xs">
                  <LimitRow label="Branches" value={p.limits.branches} />
                  <LimitRow label="Users" value={p.limits.users} />
                  <LimitRow label="AI calls" value={`${fmtNumber(p.limits.ai_calls_month || 0)}/mo`} />
                  <LimitRow label="Storage" value={`${p.limits.storage_gb} GB`} />
                </ul>
              </div>
            );
          })}

          {/* Enterprise — sales-led */}
          <div className="rounded-2xl border border-dashed bg-muted/20 p-6 flex flex-col">
            <div className="h-10 w-10 rounded-lg border bg-amber-500/10 text-amber-600 border-amber-500/20 flex items-center justify-center mb-4">
              <Building2 className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-bold">Enterprise</h3>
            <p className="text-xs text-muted-foreground mb-4 min-h-[32px]">
              Hospital chains, regulated environments, full HIPAA suite.
            </p>
            <p className="text-3xl font-bold mb-4">Custom</p>
            <a href="mailto:sales@dutilyne.com" className="mt-auto" data-testid="enterprise-contact-btn">
              <Button variant="outline" className="w-full">
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                Talk to sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ── Invoice history ── */}
      <section className="space-y-3" data-testid="invoice-history">
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          Payment history
        </h2>
        {invoices.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed py-12 text-center text-muted-foreground">
            <p className="text-sm">No invoices yet — subscribe above and your receipts will appear here.</p>
          </div>
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
              <div className="col-span-3">Date</div>
              <div className="col-span-2">Plan</div>
              <div className="col-span-2">Cycle</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2">Method</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            <div className="divide-y">
              {invoices.map((iv) => (
                <div key={iv.invoice_id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm" data-testid={`invoice-${iv.invoice_id}`}>
                  <div className="col-span-3 text-muted-foreground tabular-nums">{fmtDate(iv.paid_at || iv.created_at)}</div>
                  <div className="col-span-2 capitalize">{iv.tier}</div>
                  <div className="col-span-2 capitalize">{iv.cycle}</div>
                  <div className="col-span-2 text-right font-semibold tabular-nums">{fmtGHS(iv.amount_pesewas, true)}</div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {iv.card_brand ? `${iv.card_brand.toUpperCase()} ••${iv.card_last4}` : (iv.channel || '—')}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                      iv.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                    }`}>
                      {iv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Confirm dialog ── */}
      <Dialog open={!!confirmTier} onOpenChange={(o) => !o && setConfirmTier(null)}>
        <DialogContent data-testid="confirm-checkout-dialog">
          <DialogHeader>
            <DialogTitle>Confirm subscription change</DialogTitle>
            <DialogDescription>
              You will be redirected to Paystack to complete payment.
            </DialogDescription>
          </DialogHeader>
          {confirmTier && (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-semibold capitalize">{confirmTier.tier}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cycle</span>
                <span className="font-semibold capitalize">{confirmTier.cycle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-bold text-lg tabular-nums">{fmtGHS(confirmTier.price)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTier(null)} disabled={redirecting}>Cancel</Button>
            <Button
              onClick={() => confirmTier && startCheckout(confirmTier.tier, confirmTier.cycle)}
              disabled={redirecting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-pay-btn"
            >
              {redirecting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Continue to Paystack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Cancel dialog ── */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
            <DialogDescription>
              Your access stays active until <span className="font-semibold">{fmtDate(subscription?.current_period_end)}</span>.
              You can reactivate any time before that date.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={acting}>Keep my plan</Button>
            <Button variant="destructive" onClick={cancel} disabled={acting} data-testid="confirm-cancel-btn">
              {acting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Cancel at period end
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LimitRow({ label, value }) {
  return (
    <li className="flex items-center justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="font-medium text-foreground tabular-nums">{value}</span>
    </li>
  );
}

function CurrentPlanCard({ subscription, onCancel, onReactivate, acting, isAdmin }) {
  if (!subscription) {
    return (
      <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/30 p-6 text-center">
        <Sparkles className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          You don't have an active subscription yet. Pick a plan below to unlock all features.
        </p>
      </div>
    );
  }
  const meta = TIER_META[subscription.tier] || TIER_META.essentials;
  const Icon = meta.icon;
  const willCancel = subscription.cancel_at_period_end;
  const isPastDue = subscription.status === 'past_due';
  return (
    <div
      data-testid="current-plan-card"
      className={`rounded-2xl border bg-card p-6 ${
        isPastDue ? 'border-rose-300 bg-rose-50/30'
        : willCancel ? 'border-amber-300 bg-amber-50/30'
        : 'border-emerald-300 bg-emerald-50/30'
      }`}
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className={`h-14 w-14 rounded-2xl border ${meta.accent} flex items-center justify-center`}>
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <p className="text-xs uppercase font-semibold tracking-wide text-muted-foreground">Current plan</p>
            <p className="text-2xl font-bold capitalize flex items-center gap-2">
              {subscription.tier}
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full tracking-wide ${
                isPastDue ? 'bg-rose-500 text-white'
                : willCancel ? 'bg-amber-500 text-white'
                : 'bg-emerald-500 text-white'
              }`}>
                {isPastDue ? 'Past due' : willCancel ? 'Cancelling' : 'Active'}
              </span>
            </p>
            <p className="text-xs text-muted-foreground capitalize">{subscription.cycle} billing</p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-right">
            <p className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground flex items-center justify-end gap-1">
              <Calendar className="h-3 w-3" />
              {willCancel ? 'Ends on' : 'Renews on'}
            </p>
            <p className="text-sm font-semibold tabular-nums">{fmtDate(subscription.current_period_end)}</p>
          </div>
          {subscription.card_last4 && (
            <div className="text-right">
              <p className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground">Card</p>
              <p className="text-sm font-semibold tabular-nums">
                {(subscription.card_brand || 'card').toUpperCase()} ••{subscription.card_last4}
              </p>
            </div>
          )}
          {isAdmin && (
            <div className="flex gap-2">
              {willCancel ? (
                <Button onClick={onReactivate} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reactivate-btn">
                  {acting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Reactivate
                </Button>
              ) : (
                <Button onClick={onCancel} variant="outline" disabled={acting} data-testid="cancel-sub-btn">
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {isPastDue && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-100/50 px-3 py-2 text-xs text-rose-800 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
          We couldn't renew your subscription. Pick the same plan again below to restore service.
        </div>
      )}
    </div>
  );
}
